/**
 * OrganizationContext - Current organization state management
 *
 * Tracks the currently selected organization. Defaults to the first
 * organization when no selection is persisted. Every user has at least
 * one default organization created on signup.
 * Persists selection in localStorage. Used by Home, Databases, and
 * CreateProjectModal to scope data fetching and creation to the right owner.
 */

import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { apiJson } from '../lib/api';
import { useAuth, getAuthToken } from './AuthContext';

export interface Organization {
	id: string;
	name: string;
	slug: string;
	plan: string;
}

interface OrganizationContextValue {
	currentOrg: Organization | null;
	organizations: Organization[];
	isLoading: boolean;
	setCurrentOrg: (org: Organization | null) => void;
	refreshOrganizations: () => Promise<void>;
}

const OrganizationContext = createContext<OrganizationContextValue | null>(null);

const CURRENT_ORG_KEY = 'current_org_id';

export function OrganizationProvider({ children }: { children: ReactNode }) {
	const { isAuthenticated } = useAuth();
	const [organizations, setOrganizations] = useState<Organization[]>([]);
	const [currentOrg, setCurrentOrgState] = useState<Organization | null>(null);
	const [isLoading, setIsLoading] = useState(true);

	const fetchOrganizations = useCallback(async (): Promise<Organization[]> => {
		const token = getAuthToken();
		if (!token) return [];

		try {
			const data = await apiJson<{ success: boolean; organizations?: Organization[] }>(
				'/api/organizations'
			);
			if (data.success) {
				return data.organizations ?? [];
			}
		} catch (err) {
			console.error('Failed to fetch organizations:', err);
		}
		return [];
	}, []);

	// Load organizations and restore persisted selection on auth
	useEffect(() => {
		if (!isAuthenticated) {
			setOrganizations([]);
			setCurrentOrgState(null);
			setIsLoading(false);
			return;
		}

		(async () => {
			setIsLoading(true);
			const orgs = await fetchOrganizations();
			setOrganizations(orgs);

			// Restore persisted org selection or default to first org
			const savedOrgId = localStorage.getItem(CURRENT_ORG_KEY);
			if (savedOrgId) {
				const match = orgs.find((o) => o.id === savedOrgId);
				if (match) {
					setCurrentOrgState(match);
				} else {
					// Saved org no longer exists, default to first org
					localStorage.removeItem(CURRENT_ORG_KEY);
					const firstOrg = orgs[0];
					if (firstOrg) {
						setCurrentOrgState(firstOrg);
						localStorage.setItem(CURRENT_ORG_KEY, firstOrg.id);
					}
				}
			} else {
				// No saved org, default to first organization
				const firstOrg = orgs[0];
				if (firstOrg) {
					setCurrentOrgState(firstOrg);
					localStorage.setItem(CURRENT_ORG_KEY, firstOrg.id);
				}
			}

			setIsLoading(false);
		})();
	}, [isAuthenticated, fetchOrganizations]);

	const setCurrentOrg = useCallback((org: Organization | null) => {
		setCurrentOrgState(org);
		if (org) {
			localStorage.setItem(CURRENT_ORG_KEY, org.id);
		} else {
			localStorage.removeItem(CURRENT_ORG_KEY);
		}
	}, []);

	const refreshOrganizations = useCallback(async () => {
		const orgs = await fetchOrganizations();
		setOrganizations(orgs);

		// If current org was deleted, default to first org
		if (currentOrg && !orgs.find((o) => o.id === currentOrg.id)) {
			const firstOrg = orgs[0];
			if (firstOrg) {
				setCurrentOrg(firstOrg);
			} else {
				setCurrentOrg(null);
			}
		}
	}, [fetchOrganizations, currentOrg, setCurrentOrg]);

	return (
		<OrganizationContext.Provider
			value={{ currentOrg, organizations, isLoading, setCurrentOrg, refreshOrganizations }}
		>
			{children}
		</OrganizationContext.Provider>
	);
}

export function useOrganization() {
	const context = useContext(OrganizationContext);
	if (!context) {
		throw new Error('useOrganization must be used within OrganizationProvider');
	}
	return context;
}
